/**
 * Domain name normalisation and validation.
 *
 * Customers paste domains with protocols, trailing slashes, uppercase and
 * stray whitespace. Everything downstream (DNS lookups, Stalwart, uniqueness
 * checks) must see one canonical form.
 */

const LABEL = /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/;

/**
 * Domains we refuse because the customer does not control the mail service.
 * Accepting one would produce a domain that can never verify.
 */
const PUBLIC_EMAIL_DOMAINS = new Set([
	"gmail.com",
	"googlemail.com",
	"outlook.com",
	"hotmail.com",
	"live.com",
	"msn.com",
	"yahoo.com",
	"yahoo.co.uk",
	"ymail.com",
	"aol.com",
	"icloud.com",
	"me.com",
	"mac.com",
	"proton.me",
	"protonmail.com",
	"pm.me",
	"zoho.com",
	"gmx.com",
	"gmx.net",
	"mail.com",
	"yandex.com",
	"qq.com",
	"163.com",
	"126.com",
	"naver.com",
	"hanmail.net",
]);

/**
 * Suffixes that can never receive public mail (RFC 2606, RFC 6761).
 *
 * Rejected in production because a customer who connected one would wait
 * forever for DNS verification that cannot succeed. Development and test
 * environments allow them, since the seed data and local mail fixtures are
 * built on `.example` by design (spec §55).
 */
const RESERVED_SUFFIXES = ["localhost", "local", "internal", "invalid", "test", "example"];

export type DomainValidationError =
	| "empty"
	| "too_long"
	| "no_dot"
	| "invalid_label"
	| "invalid_tld"
	| "public_email_provider"
	| "reserved"
	| "ip_address";

export interface DomainValidationResult {
	ok: boolean;
	normalized: string;
	error?: DomainValidationError;
	/** Plain-language message intended for direct display. */
	message?: string;
}

/**
 * Strips protocol, path, port, userinfo, trailing dot and case.
 * Unicode domains are converted to their ASCII (punycode) form so that all
 * comparisons and DNS queries operate on one representation.
 */
export function normalizeDomain(input: string): string {
	let value = input.trim().toLowerCase();
	value = value.replace(/^[a-z][a-z0-9+.-]*:\/\//, "");
	value = value.replace(/^[^/@]*@/, "");
	value = value.split("/")[0] ?? "";
	value = value.split("?")[0] ?? "";
	value = value.replace(/:\d+$/, "");
	value = value.replace(/\.+$/, "");
	if (!value) return "";

	try {
		// URL performs IDNA (punycode) conversion for us.
		const asciiHost = new URL(`http://${value}`).hostname;
		return asciiHost.replace(/\.+$/, "");
	} catch {
		return value;
	}
}

export interface DomainValidationOptions {
	/**
	 * Permits documentation and test TLDs. Set only for development and test
	 * environments, never in production.
	 */
	allowReservedSuffix?: boolean;
}

/**
 * Process-wide default, so schemas that cannot take an argument still respect
 * the environment. Strict unless a process opts out at boot.
 */
let defaultOptions: DomainValidationOptions = { allowReservedSuffix: false };

export function setDomainValidationDefaults(options: DomainValidationOptions): void {
	defaultOptions = { ...defaultOptions, ...options };
}

export function validateDomain(
	input: string,
	options: DomainValidationOptions = defaultOptions,
): DomainValidationResult {
	const normalized = normalizeDomain(input);

	if (!normalized) {
		return { ok: false, normalized, error: "empty", message: "Enter a domain name." };
	}
	if (normalized.length > 253) {
		return {
			ok: false,
			normalized,
			error: "too_long",
			message: "That domain name is too long.",
		};
	}
	if (/^\d{1,3}(\.\d{1,3}){3}$/.test(normalized) || normalized.includes(":")) {
		return {
			ok: false,
			normalized,
			error: "ip_address",
			message: "Enter a domain name, not an IP address.",
		};
	}

	const labels = normalized.split(".");
	if (labels.length < 2) {
		return {
			ok: false,
			normalized,
			error: "no_dot",
			message: "Enter a full domain name, such as yourcompany.com.",
		};
	}
	for (const label of labels) {
		if (!LABEL.test(label)) {
			return {
				ok: false,
				normalized,
				error: "invalid_label",
				message: "That does not look like a valid domain name.",
			};
		}
	}

	const tld = labels[labels.length - 1] ?? "";
	if (tld.length < 2 || /^\d+$/.test(tld)) {
		return {
			ok: false,
			normalized,
			error: "invalid_tld",
			message: "That does not look like a valid domain name.",
		};
	}
	if (RESERVED_SUFFIXES.includes(tld) && !options.allowReservedSuffix) {
		return {
			ok: false,
			normalized,
			error: "reserved",
			message: "That domain is reserved and cannot be used here.",
		};
	}
	if (isPublicEmailHostname(normalized)) {
		return {
			ok: false,
			normalized,
			error: "public_email_provider",
			message:
				"That domain belongs to another email provider. Add a domain you own, such as yourcompany.com.",
		};
	}

	return { ok: true, normalized };
}

/**
 * Multi-part public suffixes we treat as the registrable tail.
 * Enough for the markets Bafe already uses; not a full PSL.
 */
const MULTI_PART_PUBLIC_SUFFIXES = new Set([
	"com.my",
	"net.my",
	"org.my",
	"edu.my",
	"gov.my",
	"co.uk",
	"org.uk",
	"ac.uk",
	"gov.uk",
	"com.au",
	"net.au",
	"org.au",
	"co.nz",
	"co.za",
	"com.br",
	"co.jp",
	"com.sg",
	"com.hk",
	"co.id",
	"com.ph",
	"co.in",
	"com.tr",
]);

/** True for a registrable apex (jages.com, company.com.my), not hub.jages.com. */
export function isApexHostname(domain: string): boolean {
	const labels = normalizeDomain(domain).split(".").filter(Boolean);
	if (labels.length < 2) return false;
	if (labels.length === 2) return true;
	const suffix2 = labels.slice(-2).join(".");
	if (MULTI_PART_PUBLIC_SUFFIXES.has(suffix2)) return labels.length === 3;
	return false;
}

/** gmail.com and mail.gmail.com are both someone else's mail service. */
export function isPublicEmailHostname(domain: string): boolean {
	const normalized = normalizeDomain(domain);
	if (PUBLIC_EMAIL_DOMAINS.has(normalized)) return true;
	const labels = normalized.split(".");
	for (let i = 1; i < labels.length - 1; i++) {
		const suffix = labels.slice(i).join(".");
		if (PUBLIC_EMAIL_DOMAINS.has(suffix)) return true;
	}
	return false;
}

const LOCAL_PART = /^[a-z0-9]([a-z0-9._+-]{0,62}[a-z0-9])?$/;

/** Operator roles customers must not claim; abuse@ on their domain is allowed. */
const RESERVED_LOCAL_PARTS = new Set(["postmaster", "hostmaster", "mailer-daemon"]);

export function normalizeLocalPart(input: string): string {
	return input.trim().toLowerCase();
}

export function validateLocalPart(input: string): { ok: boolean; normalized: string; message?: string } {
	const normalized = normalizeLocalPart(input);
	if (!normalized) return { ok: false, normalized, message: "Enter an email address." };
	if (normalized.length > 64) {
		return { ok: false, normalized, message: "That email address is too long." };
	}
	if (!LOCAL_PART.test(normalized)) {
		return {
			ok: false,
			normalized,
			message: "Use letters, numbers, dots, hyphens, plus signs and underscores only.",
		};
	}
	if (normalized.includes("..")) {
		return { ok: false, normalized, message: "Remove the repeated dots." };
	}
	return { ok: true, normalized };
}

export function isReservedLocalPart(localPart: string): boolean {
	return RESERVED_LOCAL_PARTS.has(normalizeLocalPart(localPart));
}

export function normalizeEmail(input: string): string {
	const value = input.trim().toLowerCase();
	const at = value.lastIndexOf("@");
	if (at <= 0) return value;
	const local = value.slice(0, at);
	const domain = normalizeDomain(value.slice(at + 1));
	return `${local}@${domain}`;
}
